import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { posts } from "@/data/blogData";
import useSeo from "@/lib/useSeo";
import { breadcrumbSchema } from "@/lib/schema";

export default function Resources() {
  useSeo({
    title: "Dental RCM Resources & Blog | ClariDent RCM",
    description: "Practical dental RCM guides on billing, denials, AR, insurance verification and cash flow — written by the ClariDent RCM team.",
    path: "/resources",
    schemas: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }])],
  });

  return (
    <main data-testid="resources-page">
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Resources & Blog</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900" data-testid="resources-heading">
            Dental RCM, <span className="text-teal-600">explained plainly.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Practical guides on billing, denials, AR, and running a healthier practice — written by the ClariDent team.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.1, duration: 0.5 }}
              className="rounded-2xl border border-slate-200 bg-white overflow-hidden group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="h-48 overflow-hidden">
                <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest mb-3">
                  <span className="text-teal-600">{p.category}</span>
                  <span className="text-slate-400">{p.read_time}</span>
                </div>
                <h2 className="font-display text-xl font-semibold text-navy-900 leading-snug">{p.title}</h2>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                <Link to={`/resources/${p.slug}`} data-testid={`blog-read-${p.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-teal-600 group-hover:gap-3 transition-all duration-200">
                  Read article <ArrowRight size={16} />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </main>
  );
}
